import { AlertCircle, Lock } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button, Field, Input, Select } from './ui'

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY

const MODELOS = [
  ['infoproduto', 'Infoproduto / cursos'],
  ['servico_digital', 'Serviço digital'],
  ['saas', 'SaaS / plataforma'],
  ['clinica', 'Clínica / consultório'],
  ['agencia', 'Agência'],
  ['consultoria', 'Consultoria / mentoria'],
  ['ecommerce', 'E-commerce'],
  ['outro', 'Outro'],
]
const FATURAMENTOS = [
  ['ate_30k', 'Até R$ 30 mil/mês'],
  ['30k_80k', 'R$ 30 mil a R$ 80 mil/mês'],
  ['80k_200k', 'R$ 80 mil a R$ 200 mil/mês'],
  ['acima_200k', 'Acima de R$ 200 mil/mês'],
  ['prefiro_nao_dizer', 'Prefiro não dizer'],
]
const TEMPOS = [
  ['menos_1', 'Menos de 1 ano'],
  ['1_2', '1 a 2 anos'],
  ['2_5', '2 a 5 anos'],
  ['mais_5', 'Mais de 5 anos'],
]
const URGENCIAS = [
  ['pra_ontem', 'É pra ontem'],
  ['proximo_trimestre', 'Próximo trimestre'],
  ['so_explorando', 'Só explorando por enquanto'],
]

// Captura do lead: contato + qualificacao + anti-spam (Turnstile).
export function Lead({ submitting, erro, onSubmit }) {
  const [form, setForm] = useState({
    nome: '',
    email: '',
    whatsapp: '',
    empresa: '',
    modelo_negocio: '',
    faturamento: '',
    tempo_mercado: '',
    urgencia: '',
    company_url: '', // honeypot (escondido)
  })
  const [token, setToken] = useState('')
  const tsRef = useRef(null)

  useEffect(() => {
    if (!SITE_KEY || !tsRef.current || !window.turnstile) return
    const id = window.turnstile.render(tsRef.current, {
      sitekey: SITE_KEY,
      callback: setToken,
      'expired-callback': () => setToken(''),
    })
    return () => {
      try {
        window.turnstile.remove(id)
      } catch {
        /* noop */
      }
    }
  }, [])

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)
  const podeEnviar =
    form.nome.trim() &&
    emailOk &&
    form.whatsapp.trim() &&
    form.modelo_negocio &&
    form.tempo_mercado &&
    form.urgencia &&
    (!SITE_KEY || token)

  function handle(e) {
    e.preventDefault()
    if (!podeEnviar || submitting) return
    onSubmit(form, token)
  }

  return (
    <form onSubmit={handle} className="w-full animate-fade-slide-in">
      <div className="mb-6 text-center">
        <h2 className="font-display text-2xl font-extrabold tracking-tight">Quase lá.</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Pra montar seu raio-x e mandar o resultado, preenche rapidinho:
        </p>
      </div>

      <div className="glass space-y-4 rounded-3xl p-6 md:p-8">
        <Field label="Seu nome">
          <Input
            value={form.nome}
            onChange={set('nome')}
            placeholder="Como te chamam"
            autoComplete="name"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email">
            <Input
              type="email"
              value={form.email}
              onChange={set('email')}
              placeholder="voce@empresa.com"
              autoComplete="email"
            />
          </Field>
          <Field label="WhatsApp">
            <Input
              value={form.whatsapp}
              onChange={set('whatsapp')}
              placeholder="(00) 00000-0000"
              autoComplete="tel"
            />
          </Field>
        </div>

        <Field label="Nome do negócio (opcional)">
          <Input value={form.empresa} onChange={set('empresa')} placeholder="Sua empresa" />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Tipo de negócio">
            <Select value={form.modelo_negocio} onChange={set('modelo_negocio')}>
              <option value="">Selecione</option>
              {MODELOS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Faturamento mensal">
            <Select value={form.faturamento} onChange={set('faturamento')}>
              <option value="">Selecione</option>
              {FATURAMENTOS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Tempo de mercado">
            <Select value={form.tempo_mercado} onChange={set('tempo_mercado')}>
              <option value="">Selecione</option>
              {TEMPOS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Urgência pra resolver">
            <Select value={form.urgencia} onChange={set('urgencia')}>
              <option value="">Selecione</option>
              {URGENCIAS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {/* Honeypot: escondido de gente, bot preenche. */}
        <input
          type="text"
          name="company_url"
          tabIndex={-1}
          autoComplete="off"
          value={form.company_url}
          onChange={set('company_url')}
          className="absolute left-[-9999px] h-0 w-0 opacity-0"
          aria-hidden="true"
        />

        {SITE_KEY && <div ref={tsRef} className="flex justify-center pt-1" />}

        {erro && (
          <p className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" /> {erro}
          </p>
        )}
      </div>

      <div className="mt-6 flex flex-col items-center gap-3">
        <Button
          type="submit"
          size="lg"
          disabled={!podeEnviar}
          loading={submitting}
          className="w-full sm:w-auto"
        >
          {submitting ? 'Analisando seu negócio...' : 'Ver meu resultado'}
        </Button>
        <p className="flex items-center gap-1.5 text-xs text-text-tertiary">
          <Lock className="h-3 w-3" /> Seus dados ficam com a Infosaas. Sem spam.
        </p>
      </div>
    </form>
  )
}
